/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useGlobalSearch.ts
 🗓️ Created: 2026-01-05
 🧩 Description:
    Hook for global search functionality with debouncing and API calls.

 🔧 Notes:
    - Provides search state management
    - Implements debounced API calls
    - Handles keyboard shortcuts (Ctrl+K, /)

 🔁 Last Updated: 2026-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { useMenuStore, MenuItem } from '../store/menu';
import { useI18n } from './useI18n';

export interface SearchResult {
    id: number;
    title: string;
    description?: string | null;
    url?: string | null;
    icon?: string | null;
    fullPath: string;
    type: 'module' | 'item';
    level: number;
    slug?: string;
}

export interface SearchResponse {
    modules: SearchResult[];
    items: SearchResult[];
    total: number;
}

export interface UseGlobalSearchReturn {
    isOpen: boolean;
    openSearch: () => void;
    closeSearch: () => void;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    results: SearchResponse;
    isLoading: boolean;
    handleResultSelect: (result: SearchResult) => void;
}

export function useGlobalSearch(): UseGlobalSearchReturn {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { menuItems } = useMenuStore();
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [results, setResults] = useState<SearchResponse>({ modules: [], items: [], total: 0 });
    const [isLoading, setIsLoading] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Recursively build searchable modules list from filtered menu items in the store
    const getSearchableModules = useCallback(() => {
        const modulesList: SearchResult[] = [];

        const traverse = (items: MenuItem[], pathParts: string[] = []) => {
            for (const item of items) {
                const translatedTitle = t(`menu.sections.${item.slug}`) || item.slug || '';
                const currentPath = [...pathParts, translatedTitle];

                if (item.level === 3 || (!item.hasChildren && item.url)) {
                    modulesList.push({
                        id: item.id,
                        title: translatedTitle,
                        description: item.description ? t(item.description) : null,
                        url: item.url,
                        icon: item.icon || 'folder',
                        fullPath: currentPath.join(' / '),
                        type: 'module',
                        level: item.level,
                        slug: item.slug
                    });
                }

                if (item.children && item.children.length > 0) {
                    traverse(item.children, currentPath);
                }
            }
        };

        traverse(menuItems);
        return modulesList;
    }, [menuItems, t]);

    // Debounced search function
    const performSearch = useCallback(async (query: string) => {
        // Require minimum 3 characters for search (for performance with large datasets)
        if (!query || query.trim().length < 3) {
            setResults({ modules: [], items: [], total: 0 });
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const response = await api.get('/api/search/global', {
                params: { q: query.trim(), limit: 20 }
            });
            
            // Search modules locally from the authorized menu items list
            const normalizedQuery = query.trim().toLowerCase();
            const matchedModules = getSearchableModules().filter(m => {
                return (
                    m.title.toLowerCase().includes(normalizedQuery) ||
                    m.fullPath.toLowerCase().includes(normalizedQuery) ||
                    (m.slug && m.slug.toLowerCase().includes(normalizedQuery)) ||
                    (m.description && m.description.toLowerCase().includes(normalizedQuery))
                );
            });

            const apiResults = response.data as SearchResponse;

            setResults({
                modules: matchedModules,
                items: apiResults.items || [],
                total: matchedModules.length + (apiResults.items?.length || 0)
            });
        } catch (error) {
            console.error('Search error:', error);
            // Fallback to local modules search only if API fails
            const normalizedQuery = query.trim().toLowerCase();
            const matchedModules = getSearchableModules().filter(m => {
                return (
                    m.title.toLowerCase().includes(normalizedQuery) ||
                    m.fullPath.toLowerCase().includes(normalizedQuery) ||
                    (m.slug && m.slug.toLowerCase().includes(normalizedQuery))
                );
            });
            setResults({ modules: matchedModules, items: [], total: matchedModules.length });
        } finally {
            setIsLoading(false);
        }
    }, [getSearchableModules]);

    // Handle search query changes with debounce
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        // Require minimum 3 characters before searching (for performance with large datasets)
        if (searchQuery.trim().length >= 3) {
            setIsLoading(true);
            debounceRef.current = setTimeout(() => {
                performSearch(searchQuery);
            }, 500); // 500ms debounce for better performance
        } else {
            setResults({ modules: [], items: [], total: 0 });
            setIsLoading(false);
        }

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [searchQuery, performSearch]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Ctrl+K or Cmd+K to open search
            if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
                event.preventDefault();
                setIsOpen(true);
            }

            // "/" to open search (when not in an input)
            if (event.key === '/' && !isOpen) {
                const activeElement = document.activeElement;
                const isInputActive = activeElement && (
                    activeElement.tagName === 'INPUT' ||
                    activeElement.tagName === 'TEXTAREA' ||
                    (activeElement as HTMLElement).isContentEditable
                );

                if (!isInputActive) {
                    event.preventDefault();
                    setIsOpen(true);
                }
            }

            // Escape to close
            if (event.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    const openSearch = useCallback(() => {
        setIsOpen(true);
        setSearchQuery('');
        setResults({ modules: [], items: [], total: 0 });
    }, []);

    const closeSearch = useCallback(() => {
        setIsOpen(false);
        setSearchQuery('');
        setResults({ modules: [], items: [], total: 0 });
    }, []);

    const handleResultSelect = useCallback((result: SearchResult) => {
        closeSearch();

        if (result.url) {
            navigate(result.url);
        }
    }, [navigate, closeSearch]);

    return {
        isOpen,
        openSearch,
        closeSearch,
        searchQuery,
        setSearchQuery,
        results,
        isLoading,
        handleResultSelect
    };
}
